import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { supabase } from '../../../lib/supabase';
import { Globe, Server, Shield, RefreshCw, CheckCircle, XCircle, AlertTriangle, ChevronDown, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';

interface DeploymentFormData {
  templateId: string;
  subdomain: string;
  customDomain?: string;
}

interface Template {
  id: string;
  name: string;
  description: string;
  template_type: string;
  thumbnail_url?: string;
  preview_images?: string[];
  features?: {
    icon: string;
    title: string;
    description: string;
  }[];
  is_active: boolean;
}

const DeploymentManager: React.FC = () => {
  const [formData, setFormData] = useState<DeploymentFormData>({
    templateId: '',
    subdomain: ''
  });
  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false);
  const [hoveredTemplate, setHoveredTemplate] = useState<string | null>(null);

  // Fetch templates
  const { data: templates, isLoading: isLoadingTemplates } = useQuery({
    queryKey: ['landing-page-templates'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('landing_page_templates')
        .select('*')
        .eq('is_active', true)
        .order('name');
      
      if (error) throw error;
      return data as Template[];
    }
  });

  // Fetch deployments
  const { data: deployments, refetch: refetchDeployments } = useQuery({
    queryKey: ['landing-page-deployments'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('landing_page_deployments')
        .select('*, landing_page_templates(name)')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data;
    }
  });

  // Create deployment mutation
  const createDeployment = useMutation({
    mutationFn: async (data: DeploymentFormData) => {
      const { data: deployment, error } = await supabase
        .from('landing_page_deployments')
        .insert({
          template_id: data.templateId,
          subdomain: data.subdomain,
          custom_domain: data.customDomain,
          status: 'pending'
        })
        .select()
        .single();

      if (error) throw error;

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/deploy-landing-page`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ deploymentId: deployment.id })
      });

      if (!response.ok) {
        throw new Error('Deployment failed');
      }

      return deployment;
    },
    onSuccess: () => {
      toast.success('Deployment initiated successfully');
      refetchDeployments();
      setFormData({ templateId: '', subdomain: '' });
    },
    onError: (error) => {
      toast.error(`Deployment failed: ${error.message}`);
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createDeployment.mutate(formData);
  };

  const getSelectedTemplate = () => {
    return templates?.find(t => t.id === formData.templateId);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="h-5 w-5 text-success" />;
      case 'pending':
        return <RefreshCw className="h-5 w-5 text-warning animate-spin" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-error" />;
      default:
        return <AlertTriangle className="h-5 w-5 text-warning" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Deployment Form */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-lg font-semibold mb-4">Deploy New Landing Page</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Template</label>
            <div className="relative">
              <button
                type="button"
                className="w-full bg-white border border-gray-300 rounded-md py-2 pl-3 pr-10 text-left focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                onClick={() => setShowTemplateDropdown(!showTemplateDropdown)}
                disabled={isLoadingTemplates}
              >
                {isLoadingTemplates ? (
                  <div className="flex items-center text-gray-500">
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Loading templates...
                  </div>
                ) : (
                  <span className="block truncate">
                    {getSelectedTemplate()?.name || "Select a template"}
                  </span>
                )}
                <span className="absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                </span>
              </button>

              {showTemplateDropdown && (
                <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md border border-gray-200 max-h-60 overflow-auto">
                  {isLoadingTemplates ? (
                    <div className="p-4 text-center text-gray-500">
                      <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
                      Loading templates...
                    </div>
                  ) : templates?.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">
                      No templates available
                    </div>
                  ) : (
                    templates?.map((template) => (
                      <div
                        key={template.id}
                        className="relative"
                        onMouseEnter={() => setHoveredTemplate(template.id)}
                        onMouseLeave={() => setHoveredTemplate(null)}
                      >
                        <button
                          type="button"
                          className={`w-full text-left px-4 py-2 hover:bg-gray-50 ${
                            formData.templateId === template.id ? 'bg-primary/5 text-primary' : ''
                          }`}
                          onClick={() => {
                            setFormData({ ...formData, templateId: template.id });
                            setShowTemplateDropdown(false);
                          }}
                        >
                          <div className="font-medium">{template.name}</div>
                          <div className="text-sm text-gray-500">{template.description}</div>
                        </button>

                        {hoveredTemplate === template.id && template.thumbnail_url && (
                          <div className="absolute left-full ml-2 top-0 bg-white rounded-lg shadow-xl border border-gray-200 p-2 w-64">
                            <img
                              src={template.thumbnail_url}
                              alt={template.name}
                              className="w-full h-auto rounded"
                            />
                            {template.features && template.features.length > 0 && (
                              <div className="mt-2 pt-2 border-t border-gray-100">
                                <h4 className="text-xs font-medium text-gray-700 mb-1">Key Features:</h4>
                                <ul className="text-xs text-gray-600">
                                  {template.features.slice(0, 3).map((feature, index) => (
                                    <li key={index} className="flex items-center">
                                      <CheckCircle className="h-3 w-3 text-primary mr-1" />
                                      {feature.title}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Subdomain</label>
            <div className="mt-1 flex rounded-md shadow-sm">
              <input
                type="text"
                required
                className="block w-full rounded-l-md border-gray-300 focus:border-primary focus:ring-primary"
                value={formData.subdomain}
                onChange={(e) => setFormData({ ...formData, subdomain: e.target.value })}
                placeholder="your-page"
              />
              <span className="inline-flex items-center px-3 rounded-r-md border border-l-0 border-gray-300 bg-gray-50 text-gray-500">
                .yourdomain.com
              </span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Custom Domain (Optional)
            </label>
            <input
              type="text"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
              value={formData.customDomain}
              onChange={(e) => setFormData({ ...formData, customDomain: e.target.value })}
              placeholder="www.your-domain.com"
            />
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={createDeployment.isLoading || !formData.templateId}
            >
              {createDeployment.isLoading ? 'Deploying...' : 'Deploy Landing Page'}
            </button>
          </div>
        </form>
      </div>

      {/* Deployments List */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-6">
          <h2 className="text-lg font-semibold mb-4">Active Deployments</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Template
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Domain
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    SSL
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Deployed
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {deployments?.map((deployment) => (
                  <tr key={deployment.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">
                        {deployment.landing_page_templates.name}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-900">
                          {deployment.subdomain}.yourdomain.com
                        </span>
                        {deployment.custom_domain && (
                          <span className="text-sm text-gray-500">
                            {deployment.custom_domain}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getStatusIcon(deployment.status)}
                        <span className="ml-2 text-sm text-gray-900">
                          {deployment.status}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getStatusIcon(deployment.ssl_status)}
                        <span className="ml-2 text-sm text-gray-900">
                          {deployment.ssl_status}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {deployment.deployed_at
                        ? new Date(deployment.deployed_at).toLocaleDateString()
                        : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeploymentManager;